import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import HubLanding from "@/pages/HubLanding";
import LandingPage from "@/pages/LandingPageBrutalist";
import AuthPage from "@/pages/AuthPage";
import VolunteerDashboard from "@/pages/VolunteerDashboard";
import ShelterDashboard from "@/pages/ShelterDashboard";
import NeedsFeed from "@/pages/NeedsFeed";
import AdminDashboard from "@/pages/AdminDashboard";
import EmergencyPersonnelDashboard from "@/pages/EmergencyPersonnelDashboard";
import LostPetsPage from "@/pages/LostPetsPage";
import GuidesPage from "@/pages/GuidesPage";
import PreparaLanding from "@/pages/PreparaLandingBrutalist";
import PreparaDashboard from "@/pages/PreparaDashboard";
import DomicileCardPage from "@/pages/DomicileCardPage";
import MochilaVidaPage from "@/pages/MochilaVidaPage";
import EmergencyPlansPage from "@/pages/EmergencyPlansPage";
import MyEmergencyPlanPage from "@/pages/MyEmergencyPlanPage";
import CalendarPage from "@/pages/prepara/CalendarPage";
import AprendeLanding from "@/pages/AprendeLandingBrutalist";
import CursoFamilias from "@/pages/CursoFamilias";
import AprendePanel from "@/pages/AprendePanel";
import AprendeKidsPage from "@/pages/AprendeKidsPage";
import CuentoLluviaPage from "@/pages/CuentoLluviaPage";
import HubLandingBrutalist from "@/pages/HubLandingBrutalist";
import ConsultoriaPage from "@/pages/ConsultoriaPage";
import CountrySelectPage from "@/pages/CountrySelectPage";
import OnboardingPage from "@/pages/OnboardingPage";
import WildlifeReportPage from "@/pages/WildlifeReportPage";
import WeatherPage from "@/pages/WeatherPage";
import ProteccionDatosPage from "@/pages/ProteccionDatosPage";
import EticaIAPage from "@/pages/EticaIAPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import RecursosGuiasPage from "@/pages/RecursosGuiasPage";
import ChatBot from "@/components/custom/ChatBot";
import { ThemeProvider } from "@/components/custom/ThemeProvider";
import { CalmModeProvider } from "@/contexts/CalmModeContext";
import BrutalistFooter from "@/components/custom/BrutalistFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export { API, BACKEND_URL };

// Active role helpers
function getStoredActiveRole(userRole) {
  const stored = localStorage.getItem("ea_active_role");
  if (stored && ["particular", "protectora", "emergencias", "admin"].includes(stored)) return stored;
  return userRole || "particular";
}

function BlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/recursos/ciudadania/guias/${slug}`} replace />;
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    if (processed) return;
    setProcessed(true);

    const hash = location.hash;
    const params = new URLSearchParams(hash.replace("#", ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/auth", { replace: true });
      return;
    }

    const savedRole = localStorage.getItem("ea_pending_role") || "particular";

    axios
      .post(`${API}/auth/session`, { session_id: sessionId, role: savedRole }, { withCredentials: true })
      .then((res) => {
        const { token, user } = res.data;
        localStorage.setItem("ea_token", token);
        localStorage.setItem("ea_user", JSON.stringify(user));
        localStorage.removeItem("ea_pending_role");
        const dashPath = user.role === "protectora" ? "/shelter" : user.role === "emergencias" ? "/emergency-dashboard" : "/dashboard";
        navigate(dashPath, { replace: true, state: { user } });
      })
      .catch(() => {
        navigate("/auth", { replace: true });
      });
  }, [location, navigate, processed]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--cream)]">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-[var(--blue-vibrant)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="font-body text-[var(--navy)]">Conectando...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading, activeRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--cream)]">
        <div className="w-10 h-10 border-4 border-[var(--blue-vibrant)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  // Admin users can access any panel
  if (activeRole === "admin") return children;

  // Role-based access using activeRole (which can be switched)
  const isVolunteer = ["particular", "voluntario"].includes(activeRole);
  if (requiredRole === "particular" && !isVolunteer) {
    if (activeRole === "emergencias") return <Navigate to="/emergency-dashboard" replace />;
    return <Navigate to="/shelter" replace />;
  }
  if (requiredRole === "protectora" && activeRole !== "protectora") {
    if (activeRole === "emergencias") return <Navigate to="/emergency-dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  if (requiredRole === "emergencias" && activeRole !== "emergencias") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppContent() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HubLandingBrutalist />} />
      <Route path="/classic" element={<HubLanding />} />
      <Route path="/conecta" element={<LandingPage />} />
      <Route path="/home" element={<Navigate to="/conecta" replace />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/needs" element={<NeedsFeed />} />
      <Route path="/lost-pets" element={<LostPetsPage />} />
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/consultoria" element={<ConsultoriaPage />} />
      
      {/* Prepara Routes */}
      <Route path="/prepara" element={<PreparaLanding />} />
      <Route
        path="/prepara/panel"
        element={
          <ProtectedRoute>
            <PreparaDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prepara/tarjeta"
        element={
          <ProtectedRoute>
            <DomicileCardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prepara/mochila"
        element={
          <ProtectedRoute>
            <MochilaVidaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prepara/plan"
        element={
          <ProtectedRoute>
            <EmergencyPlansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prepara/mi-plan"
        element={
          <ProtectedRoute>
            <MyEmergencyPlanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prepara/calendario"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      
      {/* APRENDE Routes */}
      <Route path="/aprende" element={<AprendeLanding />} />
      <Route path="/aprende/kids" element={<AprendeKidsPage />} />
      <Route path="/aprende/kids/cuento/lluvia" element={<CuentoLluviaPage />} />
      <Route
        path="/aprende/panel"
        element={
          <ProtectedRoute>
            <AprendePanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/aprende/curso/familias"
        element={
          <ProtectedRoute>
            <CursoFamilias />
          </ProtectedRoute>
        }
      />
      
      {/* Conecta Routes */}
      <Route path="/select-country" element={<CountrySelectPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/fauna" element={<WildlifeReportPage />} />
      <Route path="/tiempo" element={<WeatherPage />} />
      <Route path="/proteccion-datos" element={<ProteccionDatosPage />} />
      <Route path="/etica-ia" element={<EticaIAPage />} />
      {/* Recursos → Ciudadanía → Guías */}
      <Route path="/recursos/ciudadania/guias" element={<RecursosGuiasPage />} />
      <Route path="/recursos/ciudadania/guias/:slug" element={<BlogArticlePage />} />
      {/* Blog (separate section) */}
      <Route path="/blog" element={<BlogPage />} />
      {/* Legacy blog article redirects → guías */}
      <Route path="/blog/:slug" element={<BlogRedirect />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRole="particular">
            <VolunteerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/protectora"
        element={
          <ProtectedRoute requiredRole="protectora">
            <ShelterDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shelter"
        element={
          <ProtectedRoute requiredRole="protectora">
            <ShelterDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emergency-dashboard"
        element={
          <ProtectedRoute requiredRole="emergencias">
            <EmergencyPersonnelDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jefa-suprema"
        element={<AdminDashboard />}
      />
      <Route
        path="/admin"
        element={<AdminDashboard />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRoleState] = useState(() => {
    const stored = localStorage.getItem("ea_user");
    if (stored) {
      try { return getStoredActiveRole(JSON.parse(stored).role); } catch { return "particular"; }
    }
    return "particular";
  });

  const setActiveRole = (role) => {
    setActiveRoleState(role);
    localStorage.setItem("ea_active_role", role);
  };

  useEffect(() => {
    const stored = localStorage.getItem("ea_user");
    if (stored) {
      const u = JSON.parse(stored);
      setUser(u);
      setActiveRoleState(getStoredActiveRole(u.role));
    }
    // Verify with backend
    const token = localStorage.getItem("ea_token");
    if (token) {
      axios
        .get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        })
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("ea_user", JSON.stringify(res.data));
          setActiveRoleState(getStoredActiveRole(res.data.role));
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            localStorage.removeItem("ea_token");
            localStorage.removeItem("ea_user");
            localStorage.removeItem("ea_active_role");
            setUser(null);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const logout = () => {
    const token = localStorage.getItem("ea_token");
    axios.post(`${API}/auth/logout`, {}, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    }).catch(() => {});
    localStorage.removeItem("ea_token");
    localStorage.removeItem("ea_user");
    localStorage.removeItem("ea_active_role");
    setUser(null);
  };

  return (
    <HelmetProvider>
    <CalmModeProvider>
      <ThemeProvider>
        <AuthContext.Provider value={{ user, setUser, loading, logout, API, activeRole, setActiveRole }}>
          <BrowserRouter>
            <AppContent />
            <BrutalistFooter />
            <ChatBot />
          </BrowserRouter>
          <Toaster position="bottom-right" richColors />
        </AuthContext.Provider>
      </ThemeProvider>
    </CalmModeProvider>
    </HelmetProvider>
  );
}

export default App;
